import type { NavigateFunction } from 'react-router-dom';
import { isEmbeddedMode } from '../auth/embedMode';
import { ROUTE_PATHS } from '../routes/routePaths';
import { getGlobalNavigate } from '../util/globalNavigate';
import { toV3ShellHref } from '../util/parentShell';

export type StandardErrorRedirectState = {
  title?: string;
  content?: string;
  message?: string;
  isDocumentError?: boolean;
  isHtmlContent?: boolean;
  /** correlationId of the already-logged failure, so the error page reuses it. */
  correlationId?: string;
};

export type RedirectToStandardErrorOptions = StandardErrorRedirectState & {
  /** V3 `/error` variant. Defaults to 403 (access restricted). */
  status?: number;
  replace?: boolean;
  navigate?: NavigateFunction | null;
};

/** Same base64url encoding V3's `/error/$correlationId/$message` route expects. */
function encodeV3ErrorMessage(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCodePoint(byte);
  }
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

function buildV3ErrorPath(message: string, status: number): string {
  const encodedMessage = encodeV3ErrorMessage(message);
  return `/error/access/${encodedMessage}?status=${status}`;
}

/**
 * Embedded: open V3's existing `/error` page in the top window so the browser
 * URL updates. Standalone: SPA-navigate to V2 `/standard-error` with state.
 */
export function redirectToStandardError(
  options: RedirectToStandardErrorOptions = {}
): void {
  const {
    title,
    content,
    message,
    isDocumentError,
    isHtmlContent,
    correlationId,
    status = 403,
    replace,
    navigate: navigateOption,
  } = options;

  const description = content ?? message ?? '';

  if (isEmbeddedMode()) {
    const path = buildV3ErrorPath(description, status);
    const href = toV3ShellHref(path) ?? path;
    window.open(href, '_top');
    return;
  }

  const navigate = navigateOption ?? getGlobalNavigate();
  if (!navigate) {
    return;
  }

  void navigate(ROUTE_PATHS.STANDARD_ERROR, {
    replace,
    state: {
      title,
      content,
      message,
      isDocumentError,
      isHtmlContent,
      correlationId,
    },
  });
}
